import { Clock, Search, UserPlus } from 'lucide-react';
import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../services/db';
import { captureSilentError } from '../../services/posthog';
import { FriendRequest, User } from '../../types';

interface UserSearchProps {
  friends: User[];
  outgoingRequests: { request: FriendRequest, toUser: User }[];
  incomingRequests: { request: FriendRequest, fromUser: User }[];
  onRefresh: () => Promise<void>;
}

export const UserSearch: React.FC<UserSearchProps> = ({
  friends,
  outgoingRequests,
  incomingRequests,
  onRefresh
}) => {
  const { currentUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const handleSearchSubmit = async () => {
    if (!currentUser) return;
    const trimmedQuery = searchQuery.trim();
    setHasSearched(true);
    setSearchError('');
    setSearchResults([]);

    if (!trimmedQuery) {
        setSearchError('Enter a username to search.');
        return;
    }

    setIsSearching(true);
    try {
        const results = await db.searchUsers(trimmedQuery, currentUser.uid);
        setSearchResults(results);
        if (results.length === 0) {
            setSearchError('No users found with that exact username.');
        }
    } catch (e: any) {
        captureSilentError("UserSearch.handleSearchSubmit", e);
        setSearchError(e.message || 'Failed to search for users.');
    } finally {
        setIsSearching(false);
    }
  };

  const handleSendRequest = async (toUid: string) => {
    if (!currentUser) return;
    try {
        await db.sendFriendRequest(currentUser.uid, toUid);
    } catch (e: any) {
        captureSilentError("UserSearch.handleSendRequest", e);
        // If already pending or following, we still want to refresh the UI
        // so the button updates correctly.
    } finally {
        await onRefresh();
    }
  };

  return (
    <div className="space-y-3">
        <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold text-brand-dark-light uppercase tracking-wider">Find Friends</h4>
        </div>
        <p className="text-xs text-brand-dark-light mb-2">Adding a friend allows you to see each other's recommendations.</p>
        <div className="flex gap-2">
            <div className="relative flex-1">
                <Search className="absolute left-3 top-3.5 text-brand-dark-light" size={18} />
                <input 
                    type="text" 
                    placeholder="Search by username..." 
                    value={searchQuery}
                    onChange={e => { 
                        setSearchQuery(e.target.value.toLowerCase()); 
                        setHasSearched(false); 
                        setSearchResults([]); 
                        setSearchError(''); 
                    }}
                    onKeyDown={e => { if (e.key === 'Enter') handleSearchSubmit(); }}
                    className="w-full p-3 pl-10 bg-white border-2 border-brand-cream-dark rounded-xl outline-none focus:border-brand-red"
                />
            </div>
            <button 
                onClick={handleSearchSubmit}
                className="px-4 py-3 bg-brand-dark text-white font-bold rounded-xl shadow hover:bg-stone-700 active:scale-95 transition"
            >
                Search
            </button>
        </div>
        
        <div className="space-y-2 min-h-[120px]">
            {isSearching ? (
                <p className="text-center text-brand-dark-light text-sm py-4">Searching...</p>
            ) : hasSearched && searchResults.length > 0 ? (
                searchResults.map((user, index) => {
                    const isFollowing = friends.some(f => f.uid === user.uid);
                    
                    const isRequested = outgoingRequests.some(({ toUser }) => toUser.uid === user.uid);
                    const incomingRequest = incomingRequests.find(({ fromUser }) => fromUser.uid === user.uid);

                    return (
                        <div 
                            key={user.uid} 
                            className="flex items-center justify-between p-3 bg-white border border-brand-cream-dark rounded-xl shadow-sm animate-slide-in"
                            style={{ animationDelay: `${index * 0.05}s`, animationFillMode: 'forwards' }}
                        >
                            <div className="flex items-center gap-3">
                                <img src={user.photoURL} className="w-10 h-10 rounded-full bg-brand-cream-dark object-cover" />
                                <div>
                                    <p className="font-bold text-sm text-brand-dark">{user.displayName}</p>
                                    <p className="text-xs text-brand-dark-light">@{user.username}</p>
                                </div>
                            </div>
                            
                            {isFollowing ? (
                                    <span className="px-3 py-1 bg-brand-cream-dark text-brand-dark-light rounded-lg text-xs font-bold">Friend</span>
                            ) : isRequested ? (
                                    <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-lg text-xs font-bold flex gap-1 items-center"><Clock size={12}/> Pending</span>
                            ) : incomingRequest ? (
                                <span className="px-3 py-1 bg-brand-cream-dark text-brand-dark-light rounded-lg text-xs font-bold">Requested you</span>
                            ) : (
                                <button 
                                    onClick={() => handleSendRequest(user.uid)}
                                    className="px-3 py-1.5 bg-brand-dark text-white text-xs font-bold rounded-lg hover:bg-stone-700 flex items-center gap-1"
                                >
                                    <UserPlus size={14}/> Add
                                </button>
                            )}
                        </div>
                    )
                })
            ) : hasSearched ? (
                <div className="bg-white border border-brand-cream-dark rounded-xl p-4 text-sm text-brand-dark-light">
                    {searchError || 'No users found with that exact username.'}
                </div>
            ) : (
                <p className="text-sm text-brand-dark-light">Type the exact username and hit Enter or tap Search.</p>
            )}
        </div>
    </div>
  );
};
